import { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { GridColDef, GridEventListener, GridFooter, GridFooterContainer } from '@mui/x-data-grid'
import { Grid, Stack, useMediaQuery } from '@mui/material'
import { Link, useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import Card from '../../../shared/layout/Card'
import Avatar from '../../../shared/UI/Avatar'
import Paragraph from '../../../shared/UI/Paragraph'
import { StyledLink } from '../../../shared/UI/Link/components'
import { setActiveRoute } from '../../../redux/reducers/appSettingsReducer'
import { DashboardMyActionsResponse } from '../../../types/dashboard-my-actions-response'
import { dashboardService } from '../../../services/dashboardService'
import theme from '../../../theme/theme'
import { isMobileDown } from '../../../theme/deviceChecks'
import { MOBILE_EXCLUDED_REQUEST_TYPES } from '../../MyActionsGrid/Shared/enums'
import FullComponentLoadingIcon from '../../../shared/UI/LoadingIndicator/FullComponentLoadingIcon'
import { DashboardDataGrid } from '../../../shared/UI/DashboardDataGrid'
import NoResult from '../../../shared/UI/NoResult'
import { ProcessRowResponse } from '../../../types/process-row-response'
import { MyActionsHistoryState } from '../../../types/my-actions-history-state'
import { ParagraphWithEllipsis } from '../../../shared/UI/Paragraph/variants/ParagraphWithEllipsis'
import { LinkWithEllipsis } from '../../../shared/UI/Link/variants/LinkWithEllipsis'
import { RootStore } from '../../../redux/store'
import actionsService from '../../../services/actionsService'
import { RequestParams } from '../../../models/enhancement'
import { RequestStatusType } from '../../../utils/constants'

interface Rows {
  date: string
  employeeName: string | null
  id: number
  requestStatus: string | null
  requestType: string | null
}

// eslint-disable-next-line no-shadow
enum RequestType {
  WFM_ABSENCE = 'wfm absence',
  ABSENCE = 'absence',
  LATE = 'late',
  MAN_AUTH = 'manauth'
}

// eslint-disable-next-line no-shadow
enum RequestStatusLabel {
  WFM_ABSENCE = 'WFM Absence',
  MAN_AUTH = 'Discretionary Pay'
}

function getRequestStatus(request: {
  requestType?: string
  statusDescription?: string
}): string | null {
  if (!request.requestType?.trim()) {
    return null
  }

  const normalizedType = request.requestType.toLowerCase().trim()

  switch (normalizedType) {
    case RequestType.WFM_ABSENCE:
      return RequestStatusLabel.WFM_ABSENCE
    case RequestType.ABSENCE:
    case RequestType.LATE:
      return request.statusDescription?.trim() || null
    case RequestType.MAN_AUTH:
      return RequestStatusLabel.MAN_AUTH
    default:
      return request.requestType.trim()
  }
}

function NoRowsOverlay() {
  return <NoResult message="You have no actions" />
}

export function CustomFooterStatusComponent() {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  return (
    <GridFooterContainer>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ width: '100%' }}
      >
        <>
          <Link
            to="/myactions"
            onClick={() => {
              actionsService.userAnalytics
                .postEvent('UIClickEvent', 'DashboardMyActionsViewAllClick', window.location.pathname, window.location.hostname)
                .catch((error: unknown) => console.error('Analytics tracking error:', error))
              dispatch(setActiveRoute('/myactions'))
              navigate('/myactions', { state: { previousLocation: '/dashboard' } })
            }}
            style={{ textDecoration: 'none', color: theme.palette.primary.main }}
          >
            <StyledLink>View All</StyledLink>
          </Link>
        </>
        <GridFooter />
      </Stack>
    </GridFooterContainer>
  )
}

function DashboardMyActions() {
  const [myActions, setMyActions] = useState<DashboardMyActionsResponse>()
  const [rows, setRows] = useState<Rows[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const mobile = useMediaQuery(isMobileDown())
  const [lates, setLates] = useState<number[]>([])
  const { userSettings } = useSelector((state: RootStore) => state.appSettings)
  const enhancementsSettingOn = useMemo(() => userSettings?.hasEnhancements, [userSettings])

  const { myActionsV3 } = useSelector((state: RootStore) => state.featureToggles)

  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(4)
  const [totalRows, setTotalRows] = useState(0)
  const isInitialLoad = useRef(true)

  const dispatch = useDispatch()

  useEffect(() => {
    // Reset initial load flag when mobile state changes
    isInitialLoad.current = true
  }, [mobile])

  useEffect(() => {
    if (myActionsV3 === undefined) {
      return
    }

    setIsLoading(true)

    if (myActionsV3) {
      // On mobile, always fetch page 1 with max size to get all data for client-side pagination
      // On desktop, use the current page and pageSize from state
      // Note: 100 is the maximum pageSize allowed by the free (MIT) @mui/x-data-grid component
      const pageToUse = mobile ? 1 : page
      const pageSizeToUse = mobile ? 100 : pageSize

      const updatedRequestParams: RequestParams = {
        dateFrom: new Date('1900-01-01'),
        dateTo: new Date('2999-12-31'),
        directReportees: true,
        requestStatusId: RequestStatusType.AwaitingAction,
        enhancementsOnly: false,
        page: pageToUse,
        pageSize: pageSizeToUse,
      }

      actionsService.myActions
        .getManagerActions(updatedRequestParams)
        .then(response => {
          // Map the response data to the expected format
          const mappedRequests = response.requests
            .map(request => ({
              id: request.id,
              requestStatus: getRequestStatus(request),
              requestType: request.requestType || null,
              employeeName: request.displayName || null,
              date: request.submitDateTime,
            }))
            .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())

          const mappedResponse: DashboardMyActionsResponse = {
            userName: '',
            requests: mappedRequests,
            errors: [],
          }

          // Only update page state if on desktop, mobile uses client-side pagination
          if (!mobile) {
            setPage(response.paginationData.pageNumber)
            setPageSize(response.paginationData.pageSize)
            setTotalRows(response.paginationData.totalRows)
          }

          setMyActions(mappedResponse)
          setIsLoading(false)
        })
        .catch(error => {
          console.error('Error fetching manager actions:', error)
          setIsLoading(false)
        })
    } else if (enhancementsSettingOn !== undefined) {
      const getMyActions = enhancementsSettingOn
        ? dashboardService.getMyActionsV2
        : dashboardService.getMyActions

      getMyActions().then(e => {
        setMyActions(e)
        setIsLoading(false)
      })
    }
  }, [enhancementsSettingOn, page, pageSize, myActionsV3, mobile])

  const typeCheck = (cellValueRow: Rows) => {
    switch (cellValueRow.requestType) {
      case 'Birthday':
        return `/dashboard/birthdayrequest/${cellValueRow.id}`
      case 'Buy':
        return `/dashboard/buyrequest/${cellValueRow.id}`
      case 'Sell':
        return `/dashboard/sellrequest/${cellValueRow.id}`
      case 'Day Off':
        return `/dashboard/dayoffrequest/${cellValueRow.id}`
      case 'Work From Home':
        return `/dashboard/workfromhomerequest/${cellValueRow.id}`
      case 'Lieu':
      case 'Lieu (M)':
        return `/dashboard/lieurequest/${cellValueRow.id}`
      case 'Holiday':
        return `/dashboard/holidayrequest/${cellValueRow.id}`
      case 'Holiday (M)':
        return `/dashboard/manualholidayrequest/${cellValueRow.id}`
      case 'Shift':
        return `/dashboard/shiftrequest/${cellValueRow.id}`
      case 'Course':
        return `/dashboard/courserequest/${cellValueRow.id}`
      case 'Adjustment':
        return `/dashboard/adjustmentrequest/${cellValueRow.id}`
      case 'Twilight Shift Cover':
        return `/dashboard/twilightshiftcoverrequest/${cellValueRow.id}`
      case 'Night Shift Cover':
        return `/dashboard/nightshiftcoverrequest/${cellValueRow.id}`
      case 'Maternity':
        return `/dashboard/maternityrequest/${cellValueRow.id}`
      case 'Paternity':
        return `/dashboard/paternityrequest/${cellValueRow.id}`
      case 'On Call':
        return `/dashboard/oncallrequest/${cellValueRow.id}`
      case 'Call Out':
        return `/dashboard/calloutrequest/${cellValueRow.id}`
      case 'Night':
        return `/dashboard/nightrequest/${cellValueRow.id}`
      case 'Overtime':
        return `/dashboard/overtimerequest/${cellValueRow.id}`
      case 'Weekend':
        return `/dashboard/weekendrequest/${cellValueRow.id}`
      case 'Wfm Absence':
        return `/myactions?wfmAbsenceId=${cellValueRow.id}`
      case 'Late':
        return `/dashboard/lateoccurrence/${cellValueRow.id}`
      case 'ManAuth':
        return `/dashboard/discretionarypayrequest/${cellValueRow.id}`
      default:
        if (lates.includes(cellValueRow.id)) {
          return `/dashboard/lateoccurrence/${cellValueRow.id}`
        }
        return `/dashboard/absenceoccurrence/${cellValueRow.id}`
    }
  }

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'photo',
        headerName: '',
        width: 62,
        align: 'left',
        renderCell: cellValues => (
          <Grid item>
            <Avatar employee={cellValues.row.employeeName} />
          </Grid>
        ),
      },
      {
        field: 'employeeName',
        headerName: 'Employee Name',
        flex: 1,
        sortable: true,
        align: 'left',
        renderCell: cellValues => (
          <Stack direction="column" rowGap={0.5} sx={{ overflow: 'hidden' }}>
            <ParagraphWithEllipsis weight="bold">
              {cellValues.row.employeeName}
            </ParagraphWithEllipsis>
            <Link to="" style={{ textDecoration: 'none', color: theme.palette.primary.main }}>
              <LinkWithEllipsis>{cellValues.row.requestStatus}</LinkWithEllipsis>
            </Link>
          </Stack>
        ),
      },
      {
        field: 'date',
        headerName: 'Date',
        type: 'date',
        width: 100,
        align: 'right',
        renderCell: cellValues => (
          <Grid item>
            <Paragraph>{new Date(cellValues.row.date).toLocaleDateString(navigator.language)}</Paragraph>
          </Grid>
        ),
      },
    ],
    []
  )

  const resolveRowStatus = useCallback((status: string): string => {
    const components = status.split('/')
    if (components.length !== 2) {
      return status
    }
    return components[1]
  }, [])

  const processRow = useCallback(
    (status: string | null): ProcessRowResponse => {
      if (!status || !status.toLowerCase().includes('late')) {
        return { status, isLate: false }
      }
      return { status: resolveRowStatus(status), isLate: true }
    },
    [resolveRowStatus]
  )

  useEffect(() => {
    if (!myActions) {
      return
    }

    const myActionsBuffer: Rows[] = []
    const latesIds: number[] = []

    myActions.requests.forEach(request => {
      if (mobile && request.requestType && MOBILE_EXCLUDED_REQUEST_TYPES.includes(request.requestType)) {
        return
      }
      const { status, isLate } = processRow(request.requestStatus)
      if (isLate) {
        latesIds.push(request.id)
      }
      myActionsBuffer.push({
        date: request.date,
        employeeName: request.employeeName,
        id: request.id,
        requestStatus: status,
        requestType: request.requestType,
      })
    })

    setRows(myActionsBuffer)
    setLates(latesIds)

    // Update total rows count to reflect filtered results on mobile
    if (mobile) {
      setTotalRows(myActionsBuffer.length)
      // Only reset to page 1 on initial load, not on every data refresh
      if (isInitialLoad.current) {
        setPage(1)
        isInitialLoad.current = false
      }
    }
  }, [mobile, myActions, processRow])

  const navigate = useNavigate()
  const handleRowClick: GridEventListener<'rowClick'> = params => {
    actionsService.userAnalytics
      .postEvent('UIClickEvent', 'DashboardMyActionsRowClick', window.location.pathname, window.location.hostname)
      .catch((error: unknown) => console.error('Analytics tracking error:', error))
    navigate(typeCheck(params.row), {
      state: {
        previousLocation: '/dashboard',
        fromMyActionsDashboard: true,
      } as MyActionsHistoryState,
    })
    dispatch(setActiveRoute('/dashboard'))
  }

  return (
    <Card
      title="My Actions"
      style={{ height: '100%', boxSizing: 'border-box' }}
      testId="dashboard-my-actions"
    >
      <FullComponentLoadingIcon loading={isLoading} removeBorderRadius loadingHeight="253px">
        <DashboardDataGrid
          getRowId={row => row.id}
          rows={rows}
          columns={columns}
          disableSelectionOnClick
          headerHeight={0}
          autoHeight
          page={page - 1}
          pageSize={pageSize}
          paginationMode={mobile ? 'client' : 'server'}
          onPageChange={(newPage: number) => {
            actionsService.userAnalytics
              .postEvent('UIClickEvent', 'DashboardMyActionsPaginationClick', window.location.pathname, window.location.hostname)
              .catch((error: unknown) => console.error('Analytics tracking error:', error))
            setPage(newPage + 1)
          }}
          rowCount={totalRows}
          pagination
          density="comfortable"
          onRowClick={handleRowClick}
          components={{
            Footer: CustomFooterStatusComponent,
            NoRowsOverlay,
          }}
          sx={{
            cursor: 'pointer',
            '&.MuiDataGrid-autoHeight': {
              height: '100%',
            },
            '& .MuiDataGrid-virtualScrollerContent': {
              minHeight: '200px!important',
            },
            '& .MuiTablePagination-selectLabel, & .MuiTablePagination-select, & .MuiTablePagination-selectIcon': {
              display: 'none',
            },
          }}
        />
      </FullComponentLoadingIcon>
    </Card>
  )
}

export default DashboardMyActions
